import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { DemoService } from '../../../../demo.service';
import { GlobalFunctionsService } from '../../../../services/global-functions.service';
import { LegalDateTimeService } from '../../../../services/legal-datetime.service';
import {
  ActivityMetricsV2AgentRollup,
  ActivityMetricsV2BackendDecision,
  ActivityMetricsV2BackendMode,
  ActivityMetricsV2Breakdown,
  ActivityMetricsV2Event,
  ActivityMetricsV2FixedPointAudit,
  ActivityMetricsV2MetricAuditPoint,
  ActivityMetricsV2MetricAuditRow,
  ActivityMetricsV2MetricContributorRow,
  ActivityMetricsV2MetricDefinition,
  ActivityMetricsV2MetricEligibilityRow,
  ActivityMetricsV2MetricResult,
  ActivityMetricsV2Response,
  ActivityMetricsV2Search,
  ActivityMetricsV2StoreStatus,
  ActivityMetricsV2SubjectRollup,
} from '../../../../shared/models/activity-metrics-v2.model';
import { ActivityMetricsMetricGroupViewModel } from '../agent-performance-activity-metrics-metric-groups-widget/agent-performance-activity-metrics-metric-groups-widget.component';
import {
  ACTIVITY_METRICS_V2_CONTEXT_PANEL_TIMELINE_LIMIT,
  ACTIVITY_METRICS_V2_DEFAULT_GROUP_BY,
  ACTIVITY_METRICS_V2_DEFAULT_SOURCES,
  ActivityMetricsV2CoveragePresentation,
  buildActivityMetricsV2IndexedBoundaryNotice,
  buildActivityMetricsV2CoveragePresentation,
  resolveActivityMetricsV2IndexedThroughUtc,
} from '../activity-metrics-v2-indexed-reporting.util';

type MetricsContextType = 'job' | 'subject';

@Component({
  selector: 'app-agent-performance-activity-metrics-context-panel',
  templateUrl: './agent-performance-activity-metrics-context-panel.component.html',
  styleUrls: ['./agent-performance-activity-metrics-context-panel.component.scss']
})
export class AgentPerformanceActivityMetricsContextPanelComponent implements OnInit, OnChanges {
  @Input() contextType: MetricsContextType = 'job';
  @Input() jobId: number | string | null = null;
  @Input() subjectId: number | string | null = null;
  @Input() subjectNumber: string | null = null;
  @Input() subjectName: string | null = null;
  @Input() jobContext: any = null;
  @Input() subjectContext: any = null;
  @Input() stageSubjects: any[] = [];
  @Input() collapsedDefault = true;
  @Input() widgetTitle = 'Metrics 2.0 Audit';

  readonly backendModeOptions: Array<{ label: string; value: ActivityMetricsV2BackendMode }> = [
    { label: 'Reporting Tables', value: 'event_store' },
    { label: 'Live Tables', value: 'live' },
    { label: 'Auto', value: 'auto' },
  ];

  readonly defaultActivitySources = [...ACTIVITY_METRICS_V2_DEFAULT_SOURCES];

  readonly defaultGroupBy = [...ACTIVITY_METRICS_V2_DEFAULT_GROUP_BY];

  backendMode: ActivityMetricsV2BackendMode = 'event_store';
  response: ActivityMetricsV2Response | null = null;
  loading = false;
  reportErrorMessage: string | null = null;
  panelOpen = false;
  selectedMetricKey: string | null = null;
  selectedTimelineEvent: ActivityMetricsV2Event | null = null;
  selectedSubjectRollup: ActivityMetricsV2SubjectRollup | null = null;
  selectedAuditRecord: ActivityMetricsV2FixedPointAudit | null = null;
  selectedAttemptId: number | null = null;
  selectedSubjectId: number | null = null;
  requestedWindowFrom: string | null = null;
  requestedWindowTo: string | null = null;
  effectiveWindowFrom: string | null = null;
  effectiveWindowTo: string | null = null;
  pickerWindowFrom: Date | null = null;
  pickerWindowTo: Date | null = null;
  storeStatus: ActivityMetricsV2StoreStatus | null = null;
  indexedThroughUtc: string | null = null;
  storeStatusLoading = false;
  resolvedJobContext: any = null;
  jobContextLoading = false;
  resolvedStageSubjects: any[] = [];

  private pendingMetricKey: string | null = null;
  private pendingSubjectId: number | null = null;
  private pendingEventId: string | null = null;
  private pendingAttemptId: number | null = null;
  private defaultWindowContextVersion = 0;

  collapsedSections: Record<string, boolean> = {
    returned_detail_rows: false,
    unified_timeline: true,
    subject_rollup: false,
    agent_rollup: true,
    evidence_rows: false,
    fixed_point_provenance: false,
  };

  constructor(
    private readonly demoService: DemoService,
    public readonly gfunc: GlobalFunctionsService,
    public readonly legalDate: LegalDateTimeService,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.gfunc.setLoginSessionVars();
    const queryParams = this.route.snapshot.queryParamMap;
    this.pendingMetricKey = queryParams.get('metricKey');
    this.pendingSubjectId = this.toNumber(queryParams.get('subjectId'));
    this.pendingEventId = queryParams.get('eventId');
    this.pendingAttemptId = this.toNumber(queryParams.get('attemptId'));
    this.pickerWindowFrom = this.parseWindowParam(queryParams.get('metrics_from') || queryParams.get('when_from'));
    this.pickerWindowTo = this.parseWindowParam(queryParams.get('metrics_to') || queryParams.get('when_to'));
    this.panelOpen = queryParams.get('metrics') === '1' || !this.collapsedDefault;

    this.loadDefaultWindowContext();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.jobId || changes.jobContext || changes.subjectContext || changes.subjectId || changes.subjectNumber || changes.stageSubjects) {
      this.loadDefaultWindowContext();
      if (this.response) {
        this.response = null;
        this.selectedTimelineEvent = null;
        this.selectedSubjectRollup = null;
        this.selectedAuditRecord = null;
      }
    }
  }

  get isAdminUser(): boolean {
    return Number(this.gfunc.sessionUserLevel) <= 3;
  }

  get contextSummary(): string {
    if (this.contextType === 'subject') {
      const stageCount = this.stageSubjectNumbers.length;
      return `Subject ${this.subjectNumber || this.subjectId || '-'}${stageCount > 1 ? ` | ${stageCount} related stage records` : ''}`;
    }

    return `Job ${this.jobId || '-'}`;
  }

  get stageSubjectNumbers(): string[] {
    const values = this.getScopedStageSubjects()
      .map((stage) => stage?.subject_number)
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    if (this.subjectNumber && !values.includes(this.subjectNumber)) {
      values.push(this.subjectNumber);
    }

    return Array.from(new Set(values));
  }

  get summaryCards(): Array<{ label: string; value: number; helper: string }> {
    if (!this.response) {
      return [];
    }

    const summary = this.response.summary;
    return [
      {
        label: 'Events In Scope',
        value: summary.total_events,
        helper: `${summary.source_counts.length} active sources`,
      },
      {
        label: 'Attempts In Scope',
        value: summary.total_attempts,
        helper: 'Countable attempt/service rows only',
      },
      {
        label: 'Subjects With Activity',
        value: summary.total_subjects,
        helper: `${summary.total_jobs} jobs admitted by activity`,
      },
      {
        label: 'Agent Footprint',
        value: summary.total_agents,
        helper: `${summary.total_regions} regions resolved`,
      },
    ];
  }

  get timeline(): ActivityMetricsV2Event[] {
    return this.response?.timeline ?? [];
  }

  get subjects(): ActivityMetricsV2SubjectRollup[] {
    return this.response?.subjects ?? [];
  }

  get agents(): ActivityMetricsV2AgentRollup[] {
    return this.response?.agents ?? [];
  }

  get metricGroups(): ActivityMetricsMetricGroupViewModel[] {
    if (!this.response) {
      return [];
    }

    return (this.response.kpi_groups ?? []).map((group) => ({
      key: group.group_key,
      label: group.label,
      description: group.description ?? null,
      metrics: (group.metric_keys ?? [])
        .map((metricKey) => this.getMetricResult(metricKey))
        .filter((metric): metric is ActivityMetricsV2MetricResult => !!metric),
      breakdowns: (group.breakdown_keys ?? [])
        .map((breakdownKey) => this.getBreakdown(breakdownKey))
        .filter((breakdown): breakdown is ActivityMetricsV2Breakdown => !!breakdown),
    }));
  }

  get singleSubjectMetricAuditMap(): { [metricKey: string]: ActivityMetricsV2MetricAuditRow | undefined } {
    const metricAuditRows = this.selectedAuditRecord?.metric_audit ?? [];
    return metricAuditRows.reduce((accumulator, row) => {
      accumulator[row.metric_key] = row;
      return accumulator;
    }, {} as { [metricKey: string]: ActivityMetricsV2MetricAuditRow | undefined });
  }

  get backendDecision(): ActivityMetricsV2BackendDecision | null {
    return this.response?.diagnostics?.backend_decision ?? null;
  }

  get resolvedBackendLabel(): string {
    return this.formatBackendMode(this.backendDecision?.resolved_backend_mode ?? this.response?.diagnostics?.backend_mode ?? this.backendMode);
  }

  get backendAlertClass(): string {
    if (!this.backendDecision) {
      return 'alert alert-info';
    }

    if (!this.backendDecision.can_run) {
      return 'alert alert-danger';
    }

    return this.backendDecision.coverage_status === 'full' || this.backendDecision.will_use_event_store
      ? 'alert alert-success'
      : 'alert alert-warning';
  }

  get backendReasonText(): string | null {
    if (!this.backendDecision?.reason) {
      return null;
    }

    return this.backendDecision.reason;
  }

  get shouldShowCoveragePresentation(): boolean {
    return false;
  }

  get shouldShowBackendAlert(): boolean {
    if (!this.response || !this.backendDecision) {
      return false;
    }

    if (!this.backendDecision.can_run) {
      return true;
    }

    return (this.backendDecision.resolved_backend_mode ?? this.response?.diagnostics?.backend_mode ?? null) !== this.backendMode;
  }

  get scopedWindowSummaryText(): string {
    return `Scoped window: ${this.defaultWindowFromText} to ${this.defaultWindowToText}.`;
  }

  get indexedResultSummaryText(): string | null {
    if (!this.shouldShowCoveragePresentation) {
      return null;
    }

    const coveragePresentation = this.coveragePresentation;
    if (!coveragePresentation.hasIndexedCoverage) {
      return null;
    }

    if (!this.effectiveWindowFrom || !this.effectiveWindowTo) {
      return null;
    }

    return `Indexed results currently cover ${this.legalDate.formatAudit(this.effectiveWindowFrom, 'DD/MM/YYYY h:mm a')} to ${this.legalDate.formatAudit(this.effectiveWindowTo, 'DD/MM/YYYY h:mm a')}.`;
  }

  get selectedMetricAuditRows(): ActivityMetricsV2MetricAuditRow[] {
    if (this.selectedMetricKey && this.singleSubjectMetricAuditMap[this.selectedMetricKey]) {
      return [this.singleSubjectMetricAuditMap[this.selectedMetricKey] as ActivityMetricsV2MetricAuditRow];
    }

    return this.selectedAuditRecord?.metric_audit ?? [];
  }

  get contextWindowFromDateInput(): string {
    const window = this.resolveLifeWindow();
    return this.dateInputValue(this.legalDate.toPickerDate(window.when_from));
  }

  get contextWindowToDateInput(): string {
    const window = this.resolveLifeWindow();
    return this.dateInputValue(this.legalDate.toPickerDate(window.when_to));
  }

  get indexedThroughText(): string {
    return this.indexedThroughUtc
      ? this.legalDate.formatAudit(this.indexedThroughUtc, 'DD/MM/YYYY h:mm a')
      : 'Unknown';
  }

  get defaultWindowFromText(): string {
    const derivedWindow = this.resolveDerivedLifeWindow();
    return this.legalDate.formatAudit(derivedWindow.when_from, 'DD/MM/YYYY h:mm a');
  }

  get defaultWindowToText(): string {
    const derivedWindow = this.resolveDerivedLifeWindow();
    return this.legalDate.formatAudit(derivedWindow.when_to, 'DD/MM/YYYY h:mm a');
  }

  get latestKnownScopeBoundaryText(): string {
    const latestKnownDate = this.resolveLatestKnownScopeBoundaryDate();
    return latestKnownDate
      ? this.legalDate.formatAudit(this.legalDate.fromPickerDate(latestKnownDate), 'DD/MM/YYYY h:mm a')
      : 'Unknown';
  }

  get shouldShowIndexedCoverageBoundaryNotice(): boolean {
    const indexedThroughDate = this.legalDate.toPickerDate(this.indexedThroughUtc);
    const latestKnownDate = this.resolveLatestKnownScopeBoundaryDate();

    if (!indexedThroughDate || !latestKnownDate) {
      return false;
    }

    return latestKnownDate.getTime() > indexedThroughDate.getTime();
  }

  get indexedCoverageBoundaryNotice(): string | null {
    if (!this.shouldShowIndexedCoverageBoundaryNotice) {
      return null;
    }

    const scopeLabel = this.contextType === 'subject'
      ? 'Known records for this subject family'
      : 'Known records for this job';

    return buildActivityMetricsV2IndexedBoundaryNotice({
      scopeLabel,
      latestKnownBoundaryText: this.latestKnownScopeBoundaryText,
      indexedThroughText: this.indexedThroughText,
    });
  }

  get coveragePresentation(): ActivityMetricsV2CoveragePresentation {
    const requestedWindow = this.buildRequestModel();
    return buildActivityMetricsV2CoveragePresentation({
      status: this.storeStatus,
      requestedSources: requestedWindow.activity_sources,
      requestedWhenFrom: requestedWindow.when_from,
      requestedWhenTo: requestedWindow.when_to,
      formatDate: (value: string) => this.legalDate.formatAudit(value, 'DD/MM/YYYY h:mm a'),
      formatDuration: (value: number) => this.formatDuration(value),
    });
  }

  togglePanel(): void {
    this.panelOpen = !this.panelOpen;
    if (this.panelOpen && !this.response && !this.loading) {
      this.loadMetrics();
    }
  }

  async loadMetrics(): Promise<void> {
    if (!this.isAdminUser || !this.jobId || this.storeStatusLoading || this.jobContextLoading) {
      return;
    }

    const requestModel = this.buildRequestModel();
    this.loading = true;
    this.reportErrorMessage = null;
    this.requestedWindowFrom = requestModel.when_from;
    this.requestedWindowTo = requestModel.when_to;
    this.effectiveWindowFrom = requestModel.when_from;
    this.effectiveWindowTo = requestModel.when_to;

    try {
      if (requestModel.backend_mode === 'event_store') {
        this.storeStatusLoading = true;
        const scopedStatus = await this.loadScopedStoreStatus(requestModel);
        this.storeStatus = scopedStatus;
        this.indexedThroughUtc = this.resolveIndexedThroughUtc(scopedStatus, requestModel.activity_sources);
        this.storeStatusLoading = false;
      }

      const response = await firstValueFrom(this.demoService.getAgentActivityMetricsV2(requestModel));
      this.response = response;
      this.reportErrorMessage = null;
      this.hydrateSelectionFromResponse(response);
    } catch (error: any) {
      this.reportErrorMessage = error?.error?.detail || error?.message || 'Failed to load scoped Metrics 2.0 audit.';
    } finally {
      this.storeStatusLoading = false;
      this.loading = false;
    }
  }

  onContextWindowDateChange(boundary: 'from' | 'to', value: string): void {
    const parsedDate = this.parseDateInput(value);
    if (boundary === 'from') {
      this.pickerWindowFrom = parsedDate;
    } else {
      this.pickerWindowTo = parsedDate;
    }

    this.clearLoadedResponse();
    this.loadDefaultWindowContext();
  }

  resetContextWindow(): void {
    this.pickerWindowFrom = null;
    this.pickerWindowTo = null;
    this.clearLoadedResponse();
    this.loadDefaultWindowContext();
  }

  setBackendMode(mode: ActivityMetricsV2BackendMode): void {
    this.backendMode = mode;
    if (this.response || this.panelOpen) {
      void this.loadMetrics();
    }
  }

  selectTimelineEvent(event: ActivityMetricsV2Event): void {
    this.selectedTimelineEvent = event;
    this.selectedAttemptId = event.subject_service_id ?? null;
    this.selectedSubjectId = event.subject_id ?? null;
    this.selectedSubjectRollup = this.findSubjectRollup(event.subject_id ?? null);
    this.selectedAuditRecord = this.findAuditRecord(event.subject_id ?? null);
  }

  selectSubject(subject: ActivityMetricsV2SubjectRollup): void {
    this.selectedSubjectRollup = subject;
    this.selectedSubjectId = subject.subject_id ?? null;
    this.selectedAttemptId = subject.latest_subject_service_id ?? null;
    this.selectedAuditRecord = this.findAuditRecord(subject.subject_id ?? null);
    this.selectedTimelineEvent = this.timeline.find((event) => event.subject_id === subject.subject_id) ?? null;
  }

  selectMetric(metricKey: string): void {
    this.selectedMetricKey = metricKey;
  }

  toggleSection(sectionKey: string): void {
    this.collapsedSections[sectionKey] = !this.collapsedSections[sectionKey];
  }

  isSectionCollapsed(sectionKey: string): boolean {
    return !!this.collapsedSections[sectionKey];
  }

  getMetricDefinition(metricKey: string | null): ActivityMetricsV2MetricDefinition | undefined {
    if (!metricKey || !this.response) {
      return undefined;
    }

    return this.response.metric_definitions.find((definition) => definition.metric_key === metricKey);
  }

  getMetricResult(metricKey: string | null): ActivityMetricsV2MetricResult | undefined {
    if (!metricKey || !this.response) {
      return undefined;
    }

    return this.response.metric_results.find((result) => result.metric_key === metricKey);
  }

  getMetricEligibility(metricKey: string | null): ActivityMetricsV2MetricEligibilityRow | undefined {
    if (!metricKey || !this.response?.diagnostics?.metric_eligibility) {
      return undefined;
    }

    return this.response.diagnostics.metric_eligibility.find((metric) => metric.metric_key === metricKey);
  }

  getMetricAudit(metricKey: string | null): ActivityMetricsV2MetricAuditRow | undefined {
    if (!metricKey) {
      return undefined;
    }

    return this.singleSubjectMetricAuditMap[metricKey];
  }

  getMetricContributors(metricKey: string | null): ActivityMetricsV2MetricContributorRow[] {
    if (!metricKey || !this.response?.fixed_point_results?.length) {
      return [];
    }

    return this.response.fixed_point_results
      .map((record) => {
        const metricAudit = (record.metric_audit ?? []).find((row) => row.metric_key === metricKey);
        if (!metricAudit) {
          return null;
        }

        return {
          subject_id: record.subject_id,
          job_id: record.job_id,
          subject_number: record.subject_number,
          subject_name: record.subject_name,
          ...metricAudit,
        } as ActivityMetricsV2MetricContributorRow;
      })
      .filter((row): row is ActivityMetricsV2MetricContributorRow => !!row)
      .sort((left, right) => {
        if (left.eligible !== right.eligible) {
          return left.eligible ? -1 : 1;
        }

        const leftValue = left.value_ms ?? Number.MAX_SAFE_INTEGER;
        const rightValue = right.value_ms ?? Number.MAX_SAFE_INTEGER;
        if (leftValue !== rightValue) {
          return leftValue - rightValue;
        }

        return String(left.subject_number ?? left.subject_id ?? '').localeCompare(String(right.subject_number ?? right.subject_id ?? ''));
      })
      .slice(0, 50);
  }

  getEvidenceClass(point: ActivityMetricsV2MetricAuditPoint | null | undefined): string {
    if (!point?.timestamp) {
      return 'metrics-evidence-missing';
    }

    return point.fallback_step ? 'metrics-evidence-fallback' : 'metrics-evidence-primary';
  }

  getPointSourceText(point: ActivityMetricsV2MetricAuditPoint | null | undefined): string {
    if (!point) {
      return 'No endpoint returned.';
    }

    const source = point.source || 'unknown source';
    const sourceId = point.source_id !== null && point.source_id !== undefined ? ` #${point.source_id}` : '';
    return `${source}${sourceId}`;
  }

  formatBackendMode(mode: ActivityMetricsV2BackendMode | null | undefined): string {
    switch (mode) {
      case 'event_store':
        return 'Reporting Tables';
      case 'live':
        return 'Live Tables';
      case 'auto':
      default:
        return 'Auto';
    }
  }

  private buildRequestModel(): ActivityMetricsV2Search {
    const window = this.resolveLifeWindow();
    return this.buildRequestModelForWindow(window.when_from, window.when_to, this.backendMode);
  }

  private buildStoreStatusRequestModel(): ActivityMetricsV2Search {
    const window = this.resolveLifeWindow();
    return this.buildRequestModelForWindow(window.when_from, window.when_to, 'event_store');
  }

  private buildRequestModelForWindow(
    whenFrom: string | null,
    whenTo: string | null,
    backendMode: ActivityMetricsV2BackendMode,
  ): ActivityMetricsV2Search {
    const request: ActivityMetricsV2Search = {
      when_from: whenFrom,
      when_to: whenTo,
      limit: ACTIVITY_METRICS_V2_CONTEXT_PANEL_TIMELINE_LIMIT,
      projection: 'audit',
      backend_mode: backendMode,
      hosted_context_panel: true,
      activity_sources: [...this.defaultActivitySources],
      brand_id_group: [],
      brand_report_id_group: [],
      subject_number_group: [],
      subject_number: null,
      subject_id: null,
      job_id: this.toNumber(this.jobId),
      job_type_id_group: [],
      subject_service_type_id_group: [],
      subject_service_resolution_outcome_id_group: [],
      subject_service_outcome_id_group: [],
      state_group: [],
      postcode_group: [],
      suburb_group: [],
      group_by: [...this.defaultGroupBy],
      include_breakdowns: true,
      export_profile_key: 'timeline_audit',
      saved_file_name: `metrics-2-context-${this.contextType}-${this.jobId || this.subjectId || 'scope'}`,
    };

    if (this.contextType === 'subject') {
      const stageNumbers = this.stageSubjectNumbers;
      if (stageNumbers.length) {
        request.subject_number_group = stageNumbers;
      } else {
        request.subject_id = this.toNumber(this.subjectId);
        request.subject_number = this.subjectNumber || null;
      }
    }

    return request;
  }

  private resolveLifeWindow(): { when_from: string | null; when_to: string | null } {
    const derivedWindow = this.resolveDerivedLifeWindow();

    if (this.pickerWindowFrom || this.pickerWindowTo) {
      return {
        when_from: this.pickerWindowFrom
          ? this.legalDate.fromPickerDate(this.startOfDay(this.pickerWindowFrom))
          : derivedWindow.when_from,
        when_to: this.pickerWindowTo
          ? this.legalDate.fromPickerDate(this.endOfDay(this.pickerWindowTo))
          : derivedWindow.when_to,
      };
    }

    return {
      when_from: derivedWindow.when_from,
      when_to: derivedWindow.when_to,
    };
  }

  private resolveDerivedLifeWindow(): { from: Date; to: Date; when_from: string | null; when_to: string | null } {
    const jobAnchorDate = this.resolveJobAnchorDate();
    const fallbackDates = this.resolveFallbackStartDates();

    let from: Date;
    let to: Date;

    if (jobAnchorDate) {
      from = new Date(jobAnchorDate.getTime());
      to = this.resolveDefaultWindowEndDate(fallbackDates[fallbackDates.length - 1] ?? null);
    } else if (fallbackDates.length) {
      from = new Date(fallbackDates[0].getTime());
      to = this.resolveDefaultWindowEndDate(fallbackDates[fallbackDates.length - 1]);
    } else {
      const now = new Date();
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 180, 0, 0, 0, 0);
      to = this.resolveDefaultWindowEndDate(now);
    }

    if (to.getTime() < from.getTime()) {
      to = new Date(from.getTime());
    }

    return {
      from,
      to,
      when_from: this.legalDate.fromPickerDate(from),
      when_to: this.resolveDefaultWindowEndIso(to),
    };
  }

  private resolveJobAnchorDate(): Date | null {
    const anchorCandidates = [
      this.jobContext,
      this.resolvedJobContext,
      this.subjectContext,
      ...this.getScopedStageSubjects(),
    ];

    for (const candidate of anchorCandidates) {
      const resolved = this.coerceFirstDate(candidate, [
        'job_created_date',
        'job_created_at',
        'job_date_created',
        'created_date',
        'created_at',
        'date_created',
      ]);
      if (resolved) {
        return resolved;
      }
    }

    return null;
  }

  private resolveFallbackStartDates(): Date[] {
    return [
      this.subjectContext,
      this.jobContext,
      this.resolvedJobContext,
      ...this.getScopedStageSubjects(),
    ]
      .flatMap((item) => this.extractDateCandidates(item))
      .map((value) => this.legalDate.coercePickerDate(value))
      .filter((date): date is Date => !!date && !Number.isNaN(date.getTime()))
      .sort((left, right) => left.getTime() - right.getTime());
  }

  private resolveDefaultWindowEndDate(fallback: Date | null): Date {
    const latestKnownDate = this.resolveLatestKnownScopeBoundaryDate();
    if (latestKnownDate) {
      return new Date(latestKnownDate.getTime());
    }

    if (fallback) {
      return new Date(fallback.getTime());
    }

    if (this.indexedThroughUtc) {
      const indexedThrough = this.legalDate.toPickerDate(this.indexedThroughUtc);
      if (indexedThrough) {
        return new Date(indexedThrough.getTime());
      }
    }

    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  }

  private resolveDefaultWindowEndIso(resolvedTo: Date): string | null {
    return this.legalDate.fromPickerDate(resolvedTo);
  }

  private extractDateCandidates(item: any): unknown[] {
    if (!item) {
      return [];
    }

    return [
      item.created_date,
      item.created_at,
      item.job_created_date,
      item.job_created_at,
      item.instructions_date,
      item.date_of_instructions,
      item.instruction_date,
      item.aos_date,
      item.opened_at,
      item.job_opened_at,
      item.date_created,
      item.system_created_date,
      item.updated_date,
      item.updated_at,
      item.completed_date,
      item.completed_at,
      item.final_date,
      item.job_final_date,
      item.job_final_at,
      item.next_date,
      item.job_next_date,
      item.job_next_at,
      item.filing_date,
      item.hearing_date,
      item.judgement_date,
    ].filter((value) => value !== null && value !== undefined && value !== '');
  }

  private resolveLatestKnownScopeBoundaryDate(): Date | null {
    const candidates = [
      this.subjectContext,
      this.jobContext,
      this.resolvedJobContext,
      ...this.getScopedStageSubjects(),
      ...(this.response?.subjects ?? []),
    ]
      .flatMap((item) => this.extractIndexedBoundaryCandidates(item))
      .map((value) => this.legalDate.coercePickerDate(value))
      .filter((date): date is Date => !!date && !Number.isNaN(date.getTime()))
      .sort((left, right) => right.getTime() - left.getTime());

    return candidates[0] ?? null;
  }

  private extractIndexedBoundaryCandidates(item: any): unknown[] {
    if (!item) {
      return [];
    }

    return [
      item.last_activity_at,
      item.last_activity_date,
      item.completed_date,
      item.completed_at,
      item.closed_date,
      item.closed_at,
      item.final_date,
      item.job_final_date,
      item.job_final_at,
      item.next_date,
      item.job_next_date,
      item.job_next_at,
      item.hearing_date,
      item.judgement_date,
      item.filing_date,
      item.job_completed_date,
      item.job_completed_at,
      item.job_closed_date,
      item.job_closed_at,
      item.updated_date,
      item.updated_at,
      item.job_updated_date,
      item.job_updated_at,
      item.instructions_date,
      item.date_of_instructions,
      item.instruction_date,
      item.aos_date,
      item.opened_at,
      item.job_opened_at,
      item.created_date,
      item.created_at,
      item.job_created_date,
      item.job_created_at,
      item.date_created,
    ].filter((value) => value !== null && value !== undefined && value !== '');
  }

  private coerceFirstDate(item: any, fieldNames: string[]): Date | null {
    if (!item) {
      return null;
    }

    for (const fieldName of fieldNames) {
      const resolved = this.legalDate.coercePickerDate(item?.[fieldName]);
      if (resolved && !Number.isNaN(resolved.getTime())) {
        return resolved;
      }
    }

    return null;
  }

  private clearLoadedResponse(): void {
    this.response = null;
    this.reportErrorMessage = null;
    this.selectedTimelineEvent = null;
    this.selectedSubjectRollup = null;
    this.selectedAuditRecord = null;
    this.selectedAttemptId = null;
    this.selectedSubjectId = null;
    this.effectiveWindowFrom = null;
    this.effectiveWindowTo = null;
  }

  private loadDefaultWindowContext(): void {
    const contextVersion = ++this.defaultWindowContextVersion;
    this.storeStatus = null;
    this.indexedThroughUtc = null;
    this.storeStatusLoading = true;
    void this.bootstrapDefaultWindowContext(contextVersion);
  }

  private async bootstrapDefaultWindowContext(contextVersion: number): Promise<void> {
    await this.resolveScopedStageSubjects(contextVersion);
    if (contextVersion !== this.defaultWindowContextVersion) {
      return;
    }

    this.loadResolvedJobContext(contextVersion);
  }

  private loadResolvedJobContext(contextVersion: number): void {
    if (this.jobContext?.created_date || this.jobContext?.created_at || this.jobContext?.job_created_date) {
      this.resolvedJobContext = this.jobContext;
      void this.resolveScopedDefaultWindow(contextVersion);
      return;
    }

    const jobId = this.toNumber(this.jobId);
    if (!jobId) {
      this.resolvedJobContext = null;
      this.finishDefaultWindowContext(contextVersion, null, null);
      return;
    }

    this.jobContextLoading = true;
    this.demoService.getJobID(jobId)
      .pipe(finalize(() => {
        this.jobContextLoading = false;
      }))
      .subscribe({
        next: (data: any) => {
          if (contextVersion !== this.defaultWindowContextVersion) {
            return;
          }
          const rows = Array.isArray(data) ? data : [];
          this.resolvedJobContext = rows.length ? rows[0] : null;
          void this.resolveScopedDefaultWindow(contextVersion);
        },
        error: () => {
          if (contextVersion !== this.defaultWindowContextVersion) {
            return;
          }
          this.resolvedJobContext = null;
          void this.resolveScopedDefaultWindow(contextVersion);
        }
      });
  }

  private async resolveScopedDefaultWindow(contextVersion: number): Promise<void> {
    const requestModel = this.buildStoreStatusRequestModel();
    const status = await this.loadScopedStoreStatus(requestModel);
    const indexedThroughUtc = this.resolveIndexedThroughUtc(status, requestModel.activity_sources);
    this.finishDefaultWindowContext(contextVersion, status, indexedThroughUtc);
  }

  private finishDefaultWindowContext(
    contextVersion: number,
    status: ActivityMetricsV2StoreStatus | null,
    indexedThroughUtc: string | null,
  ): void {
    if (contextVersion !== this.defaultWindowContextVersion) {
      return;
    }

    this.storeStatus = status;
    this.indexedThroughUtc = indexedThroughUtc;
    this.storeStatusLoading = false;

    if (this.panelOpen && !this.response && !this.loading) {
      void this.loadMetrics();
    }
  }

  private async resolveScopedStageSubjects(contextVersion: number): Promise<void> {
    const explicitStageSubjects = Array.isArray(this.stageSubjects)
      ? this.stageSubjects.filter((item) => !!item)
      : [];

    if (this.contextType !== 'subject') {
      this.resolvedStageSubjects = explicitStageSubjects;
      return;
    }

    if (explicitStageSubjects.length > 1) {
      this.resolvedStageSubjects = explicitStageSubjects;
      return;
    }

    const jobId = this.toNumber(this.jobId);
    const subjectId = this.toNumber(this.subjectId);
    const subjectFileNumber = `${this.subjectContext?.file_number ?? ''}`.trim();
    const scopedSubjectNumber = `${this.subjectNumber ?? ''}`.trim();
    let subjectProgressRows = explicitStageSubjects;

    if (!jobId) {
      this.resolvedStageSubjects = explicitStageSubjects;
      return;
    }

    try {
      if (subjectFileNumber) {
        const subjectProgressData = await firstValueFrom(this.demoService.getSubjectProgress(jobId, subjectFileNumber));
        if (contextVersion !== this.defaultWindowContextVersion) {
          return;
        }

        subjectProgressRows = Array.isArray(subjectProgressData)
          ? subjectProgressData.filter((item) => !!item)
          : [];

        if (subjectProgressRows.length > 1) {
          this.resolvedStageSubjects = subjectProgressRows;
          return;
        }
      }

      const jobSubjectsData = await firstValueFrom(this.demoService.getJobsTableDataSubjects(jobId));
      if (contextVersion !== this.defaultWindowContextVersion) {
        return;
      }

      const jobSubjects = Array.isArray(jobSubjectsData)
        ? jobSubjectsData.filter((item) => !!item)
        : [];
      const linkedStageSubjects = this.resolveLinkedStageSubjects(jobSubjects, subjectId);
      if (linkedStageSubjects.length > 1) {
        this.resolvedStageSubjects = linkedStageSubjects;
        return;
      }

      if (subjectFileNumber) {
        const matchingFileSubjects = jobSubjects.filter((item) => `${item?.file_number ?? ''}`.trim() === subjectFileNumber);
        if (matchingFileSubjects.length > 1) {
          this.resolvedStageSubjects = matchingFileSubjects;
          return;
        }
      }

      if (scopedSubjectNumber) {
        const matchingSubjectNumbers = jobSubjects.filter((item) => `${item?.subject_number ?? ''}`.trim() === scopedSubjectNumber);
        if (matchingSubjectNumbers.length > 1) {
          this.resolvedStageSubjects = matchingSubjectNumbers;
          return;
        }
      }
    } catch {
      // Fall back to host-provided scope below.
    }

    this.resolvedStageSubjects = subjectProgressRows;
  }

  private async loadScopedStoreStatus(requestModel: ActivityMetricsV2Search): Promise<ActivityMetricsV2StoreStatus | null> {
    try {
      return await firstValueFrom(this.demoService.getAgentActivityMetricsV2StoreStatus(requestModel));
    } catch {
      return null;
    }
  }

  private resolveIndexedThroughUtc(
    status: ActivityMetricsV2StoreStatus | null,
    requestedSources: string[] | null | undefined = this.defaultActivitySources,
  ): string | null {
    return resolveActivityMetricsV2IndexedThroughUtc(status, requestedSources);
  }

  private getScopedStageSubjects(): any[] {
    const scopedStageSubjects = this.resolvedStageSubjects.length
      ? this.resolvedStageSubjects
      : (Array.isArray(this.stageSubjects) ? this.stageSubjects : []);

    return scopedStageSubjects.filter((item) => !!item);
  }

  private resolveLinkedStageSubjects(subjects: any[], subjectId: number | null): any[] {
    if (!subjectId || !Array.isArray(subjects) || !subjects.length) {
      return [];
    }

    const byId = new Map<number, any>();
    for (const subject of subjects) {
      const currentId = this.toNumber(subject?.subject_id);
      if (currentId) {
        byId.set(currentId, subject);
      }
    }

    let first = byId.get(subjectId);
    if (!first) {
      return [];
    }

    const walkedBack = new Set<number>([subjectId]);
    while (first) {
      const previousId = this.toNumber(first?.prev_subject_id);
      if (!previousId || walkedBack.has(previousId) || !byId.has(previousId)) {
        break;
      }

      walkedBack.add(previousId);
      first = byId.get(previousId);
    }

    const chain: any[] = [];
    const emitted = new Set<number>();
    let current = first;

    while (current) {
      const currentId = this.toNumber(current?.subject_id);
      if (!currentId || emitted.has(currentId)) {
        break;
      }

      chain.push(current);
      emitted.add(currentId);

      const nextId = this.toNumber(current?.next_subject_id);
      if (!nextId || !byId.has(nextId)) {
        break;
      }

      current = byId.get(nextId);
    }

    return chain;
  }

  private parseWindowParam(value: string | null): Date | null {
    if (!value) {
      return null;
    }

    return this.legalDate.toPickerDate(value) || this.parseDateInput(value);
  }

  private parseDateInput(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const parts = value.split('-').map((part) => Number(part));
    if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
      return null;
    }

    const [year, month, day] = parts;
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  private dateInputValue(value: Date | null): string {
    if (!value) {
      return '';
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private startOfDay(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0);
  }

  private endOfDay(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 23, 59, 59, 999);
  }

  private addDays(value: Date, days: number): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate() + days, 0, 0, 0, 0);
  }

  private formatDuration(ms: number): string {
    if (!Number.isFinite(ms) || ms <= 0) {
      return '0m';
    }

    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days}d`);
    }
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }
    if (!parts.length) {
      parts.push(`${totalSeconds}s`);
    }

    return parts.join(' ');
  }

  private hydrateSelectionFromResponse(response: ActivityMetricsV2Response): void {
    this.selectedMetricKey = this.pendingMetricKey || response.metric_results?.[0]?.metric_key || null;

    const pendingEvent = this.pendingEventId
      ? response.timeline.find((event) => event.event_id === this.pendingEventId)
      : null;
    if (pendingEvent) {
      this.selectTimelineEvent(pendingEvent);
      return;
    }

    const targetSubjectId = this.pendingSubjectId || this.toNumber(this.subjectId);
    const targetSubjectNumber = this.subjectNumber?.trim() || null;
    if (targetSubjectId) {
      const subject = response.subjects.find((row) => this.matchesSubjectRecord(row, targetSubjectId, targetSubjectNumber));
      if (subject) {
        this.selectSubject(subject);
        return;
      }

      const auditRecord = this.findAuditRecord(targetSubjectId, targetSubjectNumber);
      if (auditRecord) {
        this.selectedSubjectId = targetSubjectId;
        this.selectedAuditRecord = auditRecord;
      }
    } else if (targetSubjectNumber) {
      const subject = response.subjects.find((row) => this.matchesSubjectRecord(row, null, targetSubjectNumber));
      if (subject) {
        this.selectSubject(subject);
        return;
      }
    }

    if (this.pendingAttemptId) {
      const event = response.timeline.find((row) => row.subject_service_id === this.pendingAttemptId);
      if (event) {
        this.selectTimelineEvent(event);
        return;
      }
    }

    if (response.subjects?.length) {
      this.selectSubject(response.subjects[0]);
      return;
    }

    if (response.timeline?.length) {
      this.selectTimelineEvent(response.timeline[0]);
      return;
    }

    this.selectedTimelineEvent = null;
    this.selectedSubjectRollup = null;
    this.selectedAuditRecord = null;
    this.selectedAttemptId = null;
    this.selectedSubjectId = null;
  }

  private findSubjectRollup(subjectId: number | null, subjectNumber?: string | null): ActivityMetricsV2SubjectRollup | null {
    if (!subjectId && !subjectNumber) {
      return null;
    }

    return this.subjects.find((subject) => this.matchesSubjectRecord(subject, subjectId, subjectNumber ?? null)) ?? null;
  }

  private findAuditRecord(subjectId: number | null, subjectNumber?: string | null): ActivityMetricsV2FixedPointAudit | null {
    if ((!subjectId && !subjectNumber) || !this.response?.fixed_point_results) {
      return null;
    }

    return this.response.fixed_point_results.find((record) => this.matchesSubjectRecord(record, subjectId, subjectNumber ?? null)) ?? null;
  }

  private matchesSubjectRecord(
    record: { subject_id?: number | string | null; subject_number?: string | null },
    subjectId: number | null,
    subjectNumber: string | null,
  ): boolean {
    if (subjectId !== null && subjectId !== undefined) {
      const recordSubjectId = this.toNumber(record.subject_id);
      if (recordSubjectId !== null && recordSubjectId === subjectId) {
        return true;
      }
    }

    if (subjectNumber) {
      const recordSubjectNumber = typeof record.subject_number === 'string' ? record.subject_number.trim() : '';
      if (recordSubjectNumber && recordSubjectNumber === subjectNumber) {
        return true;
      }
    }

    return false;
  }

  private getBreakdown(breakdownKey: string): ActivityMetricsV2Breakdown | undefined {
    return this.response?.grouped_breakdowns.find((breakdown) => breakdown.breakdown_key === breakdownKey);
  }

  private toNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }
}
